import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { vouchersApi } from "../api/client.ts";
import { useAuth } from "../lib/auth.tsx";
import type { VoucherRow } from "../lib/types.ts";

type StatusFilter = VoucherRow["status"] | "";

export function VouchersPage() {
  const { tenant } = useAuth();
  const auth = { tenantId: tenant!.tenantId, idToken: tenant!.idToken };
  const [status, setStatus] = useState<StatusFilter>("");

  const list = useQuery({
    queryKey: ["vouchers", auth.tenantId, status],
    queryFn: () =>
      vouchersApi.list(auth, status ? { status, limit: 100 } : { limit: 100 }),
    refetchInterval: 15_000,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Vouchers</h2>
          <p className="text-sm text-slate-500">
            Every STK Push attempt, paid voucher, and active session. Auto-refresh
            every 15s.
          </p>
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="refunded">Refunded</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Time</th>
              <th className="px-4 py-3 font-medium">Phone</th>
              <th className="px-4 py-3 font-medium">Package</th>
              <th className="px-4 py-3 font-medium">Amount (KES)</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">M-Pesa receipt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {list.isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            )}
            {list.data?.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  No vouchers match.
                </td>
              </tr>
            )}
            {list.data?.map((v) => (
              <tr key={v.id}>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {new Date(v.createdAt).toLocaleString()}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-600">
                  {v.phone}
                </td>
                <td className="px-4 py-3 text-slate-700">
                  {v.packageNameSnapshot}
                </td>
                <td className="px-4 py-3 text-slate-600">
                  {v.priceKesSnapshot.toLocaleString()}
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={v.status} />
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">
                  {/* macAddress is set on first RADIUS auth; receipt comes from
                      payments callback — both nullable here for brevity */}
                  {v.macAddress ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: VoucherRow["status"] }) {
  const tone: Record<VoucherRow["status"], string> = {
    pending: "bg-amber-50 text-amber-700",
    paid: "bg-brand-50 text-brand-700",
    active: "bg-blue-50 text-blue-700",
    expired: "bg-slate-100 text-slate-500",
    refunded: "bg-slate-100 text-slate-500",
    failed: "bg-red-50 text-red-700",
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${tone[status]}`}
    >
      {status}
    </span>
  );
}
